/**
 * Ice Maze — text → grid cells (specs/word-maze-mode.md §6.3).
 *
 * A cell holds one **grapheme cluster**, never one `char`. That distinction is what lets the mode
 * ship in all ten languages instead of the seven Latin ones: Thai stacks vowels and tone marks on
 * a base consonant, Japanese wants one kana per cell, Korean one precomposed syllable, and German
 * `ä` may arrive as a combining pair from a translation pass. Splitting on `[...text]` would break
 * a Thai syllable across two tiles, which is not a harder puzzle — it is a misspelt one.
 *
 * Phaser-free so it can be tested in node (see segment.test.ts), like the rest of game/maze/.
 */

/** Thai vowels written BEFORE the consonant they are pronounced after. */
const THAI_LEADING_VOWELS = new Set(['เ', 'แ', 'โ', 'ใ', 'ไ']);

/**
 * Clusters that belong to the character on their LEFT although Unicode files them as base letters.
 *
 * Thai SARA AM (ำ) is the case that matters: `Intl.Segmenter` binds it to the preceding consonant
 * (UAX #29 treats it as a spacing mark) while the `\p{M}` fallback does not, because its General
 * Category is Lo. Left alone, the two paths would lay out different grids for น้ำ — the exact
 * silent divergence segment.test.ts exists to catch.
 */
const ATTACH_LEFT = new Set(['ำ']);

/**
 * `Intl.Segmenter` where it exists, a combining-mark regex where it does not.
 *
 * The fallback is not hypothetical: it is what an older WebView would take, and segment.test.ts
 * asserts the two agree on the whole picture-word pool of every language.
 */
function baseClusters(text: string, lang: string): string[] {
  const S = (Intl as unknown as { Segmenter?: typeof Intl.Segmenter }).Segmenter;
  if (typeof S === 'function') {
    return Array.from(new S(lang, { granularity: 'grapheme' }).segment(text), (s) => s.segment);
  }
  return text.match(/\P{M}\p{M}*/gu) ?? [];
}

/**
 * The cells one word occupies, left to right.
 *
 * Spaces are dropped rather than given a cell of their own: a blank tile in the middle of a word
 * would be both unreadable and un-selectable, and the handful of two-word entries in the pool
 * ("ice cream", "guinea pig") read fine closed up. Words that are still too long for the grid are
 * excluded as data instead (grid.ts `eligibleWords`).
 *
 * On top of the standard clustering, Thai leading vowels are merged onto the following cluster, so
 * a cell holds a readable unit (เก) instead of a floating vowel. `Intl.Segmenter` cannot do this
 * for us — a leading vowel is a full grapheme in its own right, it is only Thai *orthography* that
 * pairs it rightwards.
 */
export function cellsOf(text: string, lang: string): string[] {
  const raw = baseClusters(text.replace(/[\s ]+/g, ''), lang);
  const out: string[] = [];
  let pending = '';
  for (const c of raw) {
    if (THAI_LEADING_VOWELS.has(c[0]) && c.length === 1) { pending += c; continue; }
    if (ATTACH_LEFT.has(c[0]) && out.length > 0 && pending === '') { out[out.length - 1] += c; continue; }
    out.push(pending + c);
    pending = '';
  }
  // A trailing leading-vowel (malformed data) still gets a cell rather than vanishing.
  if (pending) out.push(pending);
  return out;
}

/** How many cells a word needs. */
export function cellCount(text: string, lang: string): number {
  return cellsOf(text, lang).length;
}

/**
 * Languages whose script packs a whole syllable into one cell, so a word is 2–3 cells rather than
 * 5–6. They get a lower length floor (a 3-cell minimum would delete two thirds of the Korean pool)
 * and, to keep the reading rather than the scanning hard, twice the decoys — see MAZE.decoyFactor
 * and specs/word-maze-mode.md §6.3.
 */
export function isDenseScript(lang: string): boolean {
  const base = lang.toLowerCase().split('-')[0];
  return base === 'ja' || base === 'ko' || base === 'zh';
}
